// src/pages/AdminVerificationDashboard.jsx
import React, { useEffect, useState } from "react";
import {
  fetchPendingVerifications,
  verifyUser,
  markUserUnderage,
} from "../services/adminApi";

const TIERS = ["NONE", "ENTRY", "MIDDLE", "HEADLINER"];

const badgeForTier = (t) =>
  ({
    ENTRY: "badge bg-white/10 text-slate-200",
    MIDDLE: "badge bg-brand-600/20 text-slate-100",
    HEADLINER: "badge bg-emerald-500/20 text-emerald-200",
  }[t] || "badge bg-white/10 text-slate-200");

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString() : "—");

export default function AdminVerificationDashboard() {
  const [users, setUsers] = useState([]);
  const [tierFilter, setTierFilter] = useState("");
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  async function loadData() {
    setLoading(true);
    setErr("");
    try {
      const res = await fetchPendingVerifications(page, tierFilter);
      const pg = res?.data;
      setUsers(pg?.content ?? []);
      setTotalPages(pg?.totalPages ?? 0);
    } catch {
      setErr("Failed to load users.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, tierFilter]);

  const handleVerify = async (userId) => {
    if (!window.confirm("Confirm age verification?")) return;
    try {
      await verifyUser(userId);
      loadData();
    } catch {
      alert("Failed to verify user.");
    }
  };

  const handleUnderage = async (userId) => {
    if (!window.confirm("Mark this user as underage and disable them?")) return;
    try {
      await markUserUnderage(userId);
      loadData();
    } catch {
      alert("Failed to mark as underage.");
    }
  };

  return (
    <div className="page space-y-6">
      {/* Header & Filters */}
      <section className="card">
        <div className="md:flex md:items-end md:justify-between gap-4">
          <div>
            <h1 className="font-display text-3xl md:text-4xl">Subscriber Verification</h1>
            <p className="mt-1 text-slate-300">
              Approve age verification for new subscribers.
            </p>
          </div>
          <span className="badge bg-brand-600/20 text-slate-100">
            {users.length} pending (page {page + 1}/{Math.max(totalPages, 1)})
          </span>
        </div>

        {/* Filter row */}
        <div className="mt-4 flex flex-col sm:flex-row gap-2 sm:items-center">
          <label className="inline-flex items-center gap-2">
            <span className="text-[11px] uppercase tracking-wider text-slate-400">
              Filter by tier
            </span>
            <select
              value={tierFilter}
              onChange={(e) => {
                setTierFilter(e.target.value);
                setPage(0);
              }}
              className="input"
            >
              <option value="">All tiers</option>
              {TIERS.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
          </label>
        </div>

        {err && (
          <div className="mt-3 rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-red-200">
            {err}
          </div>
        )}
      </section>

      {/* Loading skeleton */}
      {loading && (
        <section className="grid gap-4 md:grid-cols-2">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="card animate-pulse">
              <div className="h-6 w-48 bg-white/5 rounded" />
              <div className="mt-3 grid gap-2">
                <div className="h-4 w-2/3 bg-white/5 rounded" />
                <div className="h-4 w-1/2 bg-white/5 rounded" />
                <div className="h-4 w-1/3 bg-white/5 rounded" />
              </div>
              <div className="mt-4 h-8 w-32 bg-white/5 rounded" />
            </div>
          ))}
        </section>
      )}

      {/* Empty state */}
      {!loading && users.length === 0 && (
        <section className="card text-slate-300">No users pending verification.</section>
      )}

      {/* Pending list (cards) */}
      {!loading && users.length > 0 && (
        <section className="grid gap-4 md:grid-cols-2">
          {users.map((u) => (
            <article key={u.id} className="card">
              <div className="flex items-start justify-between gap-3">
                {/* Mini avatar (initial) */}
                <div className="flex items-center gap-3">
                  <div className="grid h-10 w-10 place-items-center rounded-full bg-white/10 text-white font-semibold">
                    {(u.username || u.email || "?").slice(0, 1).toUpperCase()}
                  </div>
                  <div className="min-w-0">
                    <h3 className="text-white font-medium truncate">
                      {u.username || u.email || "User"}
                    </h3>
                    <p className="text-slate-400 text-sm truncate">{u.email}</p>
                  </div>
                </div>

                <span className={badgeForTier(u.subscriptionTier)}>
                  {u.subscriptionTier || "—"}
                </span>
              </div>

              <div className="mt-3 grid gap-3 sm:grid-cols-2 text-sm text-slate-300">
                <Info label="DOB" value={fmtDate(u.birthDate)} />
                <Info label="Subscribed On" value={fmtDate(u.subscriptionStartDate)} />
              </div>

              <div className="mt-4 flex flex-wrap gap-2">
                <button
                  className="btn-primary"
                  onClick={() => handleVerify(u.id)}
                >
                  Verify
                </button>
                <button
                  className="btn-secondary bg-red-600 text-white hover:bg-red-700"
                  onClick={() => handleUnderage(u.id)}
                >
                  Underage
                </button>
              </div>
            </article>
          ))}
        </section>
      )}

      {/* Pagination */}
      {!loading && totalPages > 0 && (
        <section className="flex items-center justify-center gap-2">
          <button
            className="btn-secondary"
            onClick={() => setPage((p) => Math.max(0, p - 1))}
            disabled={page === 0}
          >
            Prev
          </button>
          <span className="text-slate-300 text-sm">
            Page <span className="text-white font-medium">{page + 1}</span> of{" "}
            <span className="text-white font-medium">{totalPages}</span>
          </span>
          <button
            className="btn-secondary"
            onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
            disabled={page >= totalPages - 1}
          >
            Next
          </button>
        </section>
      )}
    </div>
  );
}

/* ---------- bits ---------- */

function Info({ label, value }) {
  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.02] px-3 py-2">
      <div className="text-[11px] uppercase tracking-wider text-slate-400">{label}</div>
      <div className="mt-0.5 text-white font-medium">{value}</div>
    </div>
  );
}
